import { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { FileText, StopCircle, Loader2, Lightbulb, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { RelativeTime } from '@/components/ui/relative-time'
import type { Message } from '@/types/conversations'
import { removeControlTags } from '@/lib/remove-control-tags'

const REMARK_PLUGINS = [remarkGfm]
const REHYPE_PLUGINS = [rehypeHighlight]

type Verification = NonNullable<NonNullable<Message['turn_data']>['verification']>

/**
 * Says where the builder actually observed what it just claimed.
 *
 * A builder verifies inside its own sandbox against a private copy of the database.
 * Without this, "Done — the incorrect value is gone" reads identically whether it was
 * measured against the user's data or never observed at all.
 */
function VerificationBadge({ verification }: { verification: Verification }) {
  const { t } = useTranslation('conversations')
  const surface = verification.verified_against
  const evidence = verification.evidence ?? []

  const style = {
    fresh_clone: {
      Icon: ShieldCheck,
      label: t('verification.freshClone'),
      hint: t('verification.freshCloneHint'),
      className: 'text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    },
    worktree: {
      Icon: ShieldAlert,
      label: t('verification.worktree'),
      hint: t('verification.worktreeHint'),
      className: 'text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    },
    none: {
      Icon: ShieldQuestion,
      label: t('verification.none'),
      hint: t('verification.noneHint'),
      className: 'text-muted-foreground border-border',
    },
  }[surface]

  if (!style) return null
  const { Icon, label, hint, className } = style

  return (
    <div className="mt-2 pt-2 border-t border-violet-200/40 dark:border-violet-700/30">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${t('verification.title')}: ${label}`}
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition-colors hover:bg-muted/50 ${className}`}
          >
            <Icon className="h-3 w-3 shrink-0" />
            <span>{label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 text-xs">
          <p className="text-muted-foreground">{hint}</p>
          {evidence.length > 0 && (
            <div className="mt-2 pt-2 border-t">
              <div className="font-medium mb-1">{t('verification.observed')}</div>
              <ul className="space-y-1">
                {evidence.map((e, i) => (
                  <li key={i} className="flex flex-col">
                    <span>
                      <span className="text-muted-foreground">{e.label}: </span>
                      <span className="font-mono">{e.value}</span>
                    </span>
                    <span className="text-[10px] text-muted-foreground">{e.source}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}

function ResponseLogPopover({ texts, containerRef }: {
  texts: string[]
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const triggerBtnRef = useRef<HTMLButtonElement>(null)
  const [alignOffset, setAlignOffset] = useState(-76)
  const [open, setOpen] = useState(false)

  const recalcOffset = () => {
    if (!triggerBtnRef.current || !containerRef.current) return
    const triggerLeft = triggerBtnRef.current.getBoundingClientRect().left
    const containerLeft = containerRef.current.getBoundingClientRect().left
    setAlignOffset(containerLeft - triggerLeft)
  }

  useEffect(() => {
    if (!open) return
    const scrollEl = triggerBtnRef.current?.closest('[data-radix-scroll-area-viewport]') as HTMLElement | null
    if (!scrollEl) return
    const handleScroll = () => setOpen(false)
    scrollEl.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', handleScroll)
  }, [open])

  return (
    <Popover open={open} onOpenChange={(o) => { if (o) recalcOffset(); setOpen(o) }}>
      <PopoverTrigger asChild>
        <button ref={triggerBtnRef} className="inline-flex items-center gap-1 leading-none text-muted-foreground/60 hover:text-foreground transition-colors duration-200">
          <FileText className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        alignOffset={alignOffset}
        collisionPadding={16}
        className="p-0 backdrop-blur-lg bg-background/50 border-border/50"
        style={containerRef.current ? { width: containerRef.current.offsetWidth } : undefined}
      >
        <div className="px-4 pt-3 pb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <FileText className="h-3 w-3" />
          Response log:
        </div>
        <ScrollArea className="max-h-96 overflow-auto">
          <div className="px-4 pb-4 pt-2 space-y-3">
            {texts.map((text, i) => (
              <div key={i} className="text-xs text-muted-foreground min-w-0 [&_p]:m-0 [&_code]:text-[10px] [&_pre]:text-[10px]">
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                  {text}
                </ReactMarkdown>
              </div>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

interface TurnSummaryMessageProps {
  message: Message
  isOrphaned?: boolean
  collapsedMessages?: Message[]
  onSuggestionClick?: (prompt: string) => void
}

export function TurnSummaryMessage({
  message,
  isOrphaned = false,
  collapsedMessages = [],
  onSuggestionClick,
}: TurnSummaryMessageProps) {
  const summary = message.turn_data?.summary
  const suggestions = message.turn_data?.nextStepSuggestions
  const taskTitle = message.turn_data?.taskTitle
  const verification = message.turn_data?.verification
  const containerRef = useRef<HTMLDivElement>(null)

  const collapsedTexts = collapsedMessages
    .filter(m => m.content && m.role === 'assistant')
    .map(m => removeControlTags(m.content).trim())
    .filter(text => text.length > 0)

  // Interrupted turn
  if (message.status === 'interrupted') {
    return (
      <div
        ref={containerRef}
        className={`mt-4 ${isOrphaned ? 'opacity-50' : ''}`}
      >
        <div className="flex flex-col gap-1 min-w-0 animate-in fade-in duration-300">
          <div className="pl-4 py-3 border-l-2 border-violet-400/60 dark:border-violet-500/40 bg-violet-50/40 dark:bg-violet-950/20 rounded-r-lg">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <StopCircle className="h-3 w-3" />
              <span>Interrupted</span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <RelativeTime
                timestamp={message.created_at}
                className="text-xs text-muted-foreground"
              />
              {collapsedTexts.length > 0 && (
                <ResponseLogPopover texts={collapsedTexts} containerRef={containerRef} />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Summary available — purple message-to-user container
  if (summary) {
    return (
      <div
        ref={containerRef}
        className={`mt-4 ${isOrphaned ? 'opacity-50' : ''}`}
      >
        <div className="flex flex-col gap-1 min-w-0 animate-in fade-in duration-300">
          <div className="pl-4 py-3 border-l-2 border-violet-400/60 dark:border-violet-500/40 bg-violet-50/40 dark:bg-violet-950/20 rounded-r-lg">
            {taskTitle && (
              <div className="mb-1.5">
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-3.5 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-700">
                  {taskTitle}
                </Badge>
              </div>
            )}
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS}>
                {summary}
              </ReactMarkdown>
            </div>
            {verification && <VerificationBadge verification={verification} />}
            <div className="flex items-center gap-3 mt-1">
              <RelativeTime
                timestamp={message.created_at}
                className="text-xs text-muted-foreground"
              />
              {collapsedTexts.length > 0 && (
                <ResponseLogPopover texts={collapsedTexts} containerRef={containerRef} />
              )}
            </div>
            {suggestions && suggestions.length > 0 && onSuggestionClick && (
              <div className="mt-2.5 pt-2 border-t border-violet-200/40 dark:border-violet-700/30">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1.5">
                  <Lightbulb className="h-3 w-3" />
                  <span>What's next</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.filter(s => s.type !== 'test').map((s, i) => (
                    <button
                      key={i}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-violet-200 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors"
                      onClick={() => onSuggestionClick(s.prompt)}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Waiting for summary — minimal loading in same purple container
  return (
    <div className={`mt-4 ${isOrphaned ? 'opacity-50' : ''}`}>
      <div className="pl-4 py-3 border-l-2 border-violet-400/60 dark:border-violet-500/40 bg-violet-50/40 dark:bg-violet-950/20 rounded-r-lg">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Generating summary...</span>
        </div>
      </div>
    </div>
  )
}
